/**
 * Default Banking Partner Configuration
 *
 * This configuration is used as a fallback when a source doesn't have
 * bankingPartnerConfigurations defined. It provides complete field settings,
 * document requirements, and section configurations for both low-risk and
 * high-risk industry classifications.
 */

import type { BankingPartnerConfiguration } from '@/features/merchant-application/types/fieldSettings';

export const DEFAULT_BANKING_PARTNER_CONFIG: BankingPartnerConfiguration = {
  "low-risk": {
    "documentRequirements": [
      {
        "maxFileSize": 5,
        "label": "Driver's License",
        "acceptedFormats": ["jpg", "jpeg", "png", "pdf"],
        "documentType": "drivers_license",
        "description": "Valid government-issued driver's license",
        "enabled": true,
        "required": true
      },
      {
        "description": "Voided check from your business bank account",
        "required": true,
        "maxFileSize": 5,
        "label": "Voided Check",
        "enabled": true,
        "documentType": "voided_check",
        "acceptedFormats": ["jpg", "jpeg", "png", "pdf"]
      },
      {
        "processingVolumeThreshold": 50000,
        "required": false,
        "maxFileSize": 10,
        "documentType": "bank_statements",
        "acceptedFormats": ["pdf"],
        "label": "Bank Statements",
        "enabled": true,
        "description": "Last 3 months of business bank statements"
      },
      {
        "required": false,
        "label": "Business License",
        "description": "Business license or registration",
        "acceptedFormats": ["jpg", "jpeg", "png", "pdf"],
        "enabled": true,
        "maxFileSize": 5,
        "documentType": "business_license"
      },
      {
        "acceptedFormats": ["pdf"],
        "processingVolumeThreshold": 100000,
        "documentType": "tax_returns",
        "label": "Tax Returns",
        "maxFileSize": 20,
        "description": "Last 2 years of business tax returns",
        "required": false,
        "enabled": false
      },
      {
        "documentType": "processing_statements",
        "required": false,
        "label": "Processing Statements",
        "maxFileSize": 10,
        "description": "Last 3 months of credit card processing statements (if currently processing)",
        "acceptedFormats": ["pdf"],
        "enabled": true
      }
    ],
    "sectionOrder": [
      "Personal Information",
      "Corporation Information",
      "Beneficial Owners",
      "Bank Details",
      "Documentation"
    ],
    "sectionVisibility": {
      "Personal Information": true,
      "Processing History": false,
      "Beneficial Owners": true,
      "Documentation": true,
      "Corporation Information": true,
      "Bank Details": true
    },
    "fieldSettings": {
      "bankDetails.accountType": {
        "fieldName": "accountType",
        "fieldPath": "bankDetails.accountType",
        "defaultValue": "checking",
        "order": 4,
        "enabled": true,
        "required": true,
        "helpText": "Select whether the settlement account is checking or savings.",
        "label": "Bank Account Type",
        "section": "Bank Details"
      },
      "processingHistory.monthlyVolume": {
        "enabled": true,
        "order": 2,
        "required": true,
        "placeholder": "10000",
        "fieldPath": "processingHistory.monthlyVolume",
        "helpText": "Average monthly processing volume in dollars",
        "fieldName": "monthlyVolume",
        "label": "Monthly Processing Volume",
        "section": "Processing History"
      },
      "processingHistory.terminationExplanation": {
        "fieldName": "terminationExplanation",
        "placeholder": "Please explain the circumstances",
        "label": "Termination Explanation",
        "fieldPath": "processingHistory.terminationExplanation",
        "enabled": true,
        "required": true,
        "order": 17,
        "section": "Processing History"
      },
      "processingHistory.salesDeliveryDays": {
        "helpText": "Typical number of days between sale and delivery",
        "placeholder": "0",
        "fieldPath": "processingHistory.salesDeliveryDays",
        "fieldName": "salesDeliveryDays",
        "order": 5,
        "required": true,
        "label": "Average Sales Delivery Days",
        "defaultValue": "0",
        "enabled": true,
        "section": "Processing History"
      },
      "processingHistory.depositPct": {
        "fieldPath": "processingHistory.depositPct",
        "fieldName": "depositPct",
        "order": 9,
        "defaultValue": "0",
        "required": true,
        "enabled": true,
        "label": "Deposit Percentage (%)",
        "placeholder": "0",
        "validation": {
          "max": 100,
          "min": 0
        },
        "helpText": "Percentage of the order amount collected upfront.",
        "section": "Processing History"
      },
      "beneficialOwners.driverLicense": {
        "enabled": true,
        "fieldPath": "beneficialOwners.driverLicense",
        "placeholder": "DL123456789",
        "section": "Beneficial Owners",
        "order": 9,
        "fieldName": "driverLicense",
        "label": "Driver's License Number",
        "required": true
      },
      "beneficialOwners.city": {
        "enabled": true,
        "required": true,
        "order": 11,
        "fieldPath": "beneficialOwners.city",
        "fieldName": "city",
        "label": "City",
        "placeholder": "City",
        "section": "Beneficial Owners"
      },
      "processingHistory.onlinePercentage": {
        "helpText": "Percentage of transactions processed online (auto-calculated from retail percentage)",
        "order": 13,
        "required": true,
        "placeholder": "10",
        "enabled": true,
        "label": "Online Transaction Percentage",
        "defaultValue": "10",
        "fieldPath": "processingHistory.onlinePercentage",
        "fieldName": "onlinePercentage",
        "section": "Processing History"
      },
      "processingHistory.highTicket": {
        "fieldPath": "processingHistory.highTicket",
        "order": 4,
        "required": true,
        "enabled": true,
        "label": "Highest Transaction Amount",
        "fieldName": "highTicket",
        "placeholder": "1000",
        "section": "Processing History"
      },
      "processingHistory.advancedDeposits": {
        "order": 8,
        "defaultValue": "no",
        "label": "Collect Advanced Deposits?",
        "required": true,
        "fieldPath": "processingHistory.advancedDeposits",
        "enabled": true,
        "helpText": "Do you collect deposits before fulfillment?",
        "fieldName": "advancedDeposits",
        "section": "Processing History"
      },
      "beneficialOwners.phone": {
        "fieldPath": "beneficialOwners.phone",
        "required": true,
        "section": "Beneficial Owners",
        "label": "Phone Number",
        "order": 6,
        "fieldName": "phone",
        "enabled": true,
        "placeholder": "(XXX) XXX-XXXX"
      },
      "personalInfo.firstName": {
        "required": true,
        "enabled": true,
        "placeholder": "John",
        "fieldPath": "personalInfo.firstName",
        "order": 1,
        "label": "First Name",
        "fieldName": "firstName",
        "section": "Personal Information"
      },
      "processingHistory.bankruptcy": {
        "defaultValue": "false",
        "required": true,
        "fieldPath": "processingHistory.bankruptcy",
        "helpText": "Have you or your business filed for bankruptcy in the past 7 years?",
        "fieldName": "bankruptcy",
        "order": 18,
        "enabled": true,
        "label": "Any History of Bankruptcy?",
        "section": "Processing History"
      },
      "corporationInfo.dba": {
        "required": true,
        "placeholder": "Optional",
        "fieldName": "dba",
        "section": "Corporation Information",
        "fieldPath": "corporationInfo.dba",
        "order": 2,
        "label": "DBA (Doing Business As)",
        "enabled": true
      },
      "bankDetails.routingNumber": {
        "required": true,
        "section": "Bank Details",
        "fieldPath": "bankDetails.routingNumber",
        "enabled": true,
        "label": "Routing Number",
        "order": 2,
        "fieldName": "routingNumber",
        "placeholder": "123456789"
      },
      "corporationInfo.sicDescription": {
        "required": true,
        "section": "Corporation Information",
        "fieldPath": "corporationInfo.sicDescription",
        "order": 9,
        "fieldName": "sicDescription",
        "enabled": true,
        "placeholder": "Auto-populated description",
        "label": "SIC Code Description",
        "helpText": "Auto-populated description of the SIC code"
      },
      "personalInfo.lastName": {
        "order": 2,
        "required": true,
        "fieldName": "lastName",
        "section": "Personal Information",
        "enabled": true,
        "label": "Last Name",
        "fieldPath": "personalInfo.lastName",
        "placeholder": "Doe"
      },
      "processingHistory.isCurrentlyProcessing": {
        "order": 1,
        "label": "Currently Processing Payments?",
        "fieldName": "isCurrentlyProcessing",
        "placeholder": "Yes/No",
        "fieldPath": "processingHistory.isCurrentlyProcessing",
        "required": true,
        "enabled": true,
        "section": "Processing History"
      },
      "beneficialOwners.ssn": {
        "placeholder": "XXX-XX-XXXX",
        "enabled": true,
        "order": 7,
        "fieldPath": "beneficialOwners.ssn",
        "required": true,
        "label": "Social Security Number",
        "section": "Beneficial Owners",
        "fieldName": "ssn"
      },
      "corporationInfo.businessName": {
        "required": true,
        "enabled": true,
        "fieldPath": "corporationInfo.businessName",
        "label": "Business Legal Name",
        "fieldName": "businessName",
        "order": 1,
        "placeholder": "ABC Company LLC",
        "section": "Corporation Information"
      },
      "processingHistory.averageTicket": {
        "enabled": true,
        "placeholder": "100",
        "required": true,
        "fieldName": "averageTicket",
        "order": 3,
        "fieldPath": "processingHistory.averageTicket",
        "label": "Average Transaction Amount",
        "section": "Processing History"
      },
      "processingHistory.whenIsCustomerCharged": {
        "defaultValue": "order",
        "enabled": true,
        "required": true,
        "order": 6,
        "fieldName": "whenIsCustomerCharged",
        "label": "When do you charge the credit card?",
        "helpText": "Select when the customer is charged during the order lifecycle.",
        "fieldPath": "processingHistory.whenIsCustomerCharged",
        "section": "Processing History"
      },
      "corporationInfo.businessDescription": {
        "placeholder": "Describe what your business does...",
        "fieldName": "businessDescription",
        "required": true,
        "label": "Business Description",
        "enabled": true,
        "order": 7,
        "section": "Corporation Information",
        "fieldPath": "corporationInfo.businessDescription"
      },
      "corporationInfo.businessAddress.street": {
        "fieldPath": "corporationInfo.businessAddress.street",
        "enabled": true,
        "order": 13,
        "label": "Street Address",
        "required": true,
        "placeholder": "123 Main Street",
        "section": "Corporation Information",
        "fieldName": "street"
      },
      "bankDetails.accountNumber": {
        "enabled": true,
        "section": "Bank Details",
        "placeholder": "XXXXXXXXXXXX",
        "fieldName": "accountNumber",
        "label": "Account Number",
        "required": true,
        "fieldPath": "bankDetails.accountNumber",
        "order": 3
      },
      "corporationInfo.customerServicePhone": {
        "fieldName": "customerServicePhone",
        "linkEnabled": true,
        "placeholder": "(XXX) XXX-XXXX",
        "fieldPath": "corporationInfo.customerServicePhone",
        "enabled": true,
        "order": 11,
        "required": true,
        "section": "Corporation Information",
        "label": "Customer Service Phone"
      },
      "beneficialOwners.ownershipPercentage": {
        "fieldPath": "beneficialOwners.ownershipPercentage",
        "fieldName": "ownershipPercentage",
        "label": "Ownership Percentage",
        "order": 4,
        "helpText": "Percentage of business ownership",
        "enabled": true,
        "placeholder": "25",
        "required": true,
        "section": "Beneficial Owners"
      },
      "beneficialOwners.zipCode": {
        "fieldName": "zipCode",
        "section": "Beneficial Owners",
        "fieldPath": "beneficialOwners.zipCode",
        "label": "ZIP Code",
        "placeholder": "12345",
        "required": true,
        "enabled": true,
        "order": 13
      },
      "beneficialOwners.address": {
        "fieldPath": "beneficialOwners.address",
        "order": 10,
        "required": true,
        "enabled": true,
        "label": "Address",
        "fieldName": "address",
        "section": "Beneficial Owners",
        "placeholder": "123 Main Street"
      },
      "processingHistory.acceptOfflineCards": {
        "defaultValue": "false",
        "fieldName": "acceptOfflineCards",
        "label": "Accept Offline/Terminal Card Payments?",
        "enabled": true,
        "required": true,
        "order": 11,
        "helpText": "Do you accept credit cards through physical terminals?",
        "fieldPath": "processingHistory.acceptOfflineCards",
        "section": "Processing History"
      },
      "processingHistory.currentProcessor": {
        "fieldName": "currentProcessor",
        "order": 10,
        "label": "Current Payment Processor",
        "placeholder": "Stripe, Square, etc.",
        "required": true,
        "enabled": true,
        "fieldPath": "processingHistory.currentProcessor",
        "section": "Processing History"
      },
      "beneficialOwners.lastName": {
        "fieldName": "lastName",
        "section": "Beneficial Owners",
        "order": 2,
        "enabled": true,
        "label": "Last Name",
        "fieldPath": "beneficialOwners.lastName",
        "placeholder": "Doe",
        "required": true
      },
      "corporationInfo.businessAddress.city": {
        "placeholder": "City",
        "label": "City",
        "section": "Corporation Information",
        "order": 14,
        "fieldName": "city",
        "required": true,
        "enabled": true,
        "fieldPath": "corporationInfo.businessAddress.city"
      },
      "corporationInfo.ein": {
        "required": true,
        "fieldPath": "corporationInfo.ein",
        "section": "Corporation Information",
        "fieldName": "ein",
        "placeholder": "XX-XXXXXXX",
        "label": "EIN (Employer Identification Number)",
        "enabled": true,
        "order": 5
      },
      "beneficialOwners.state": {
        "fieldName": "state",
        "order": 12,
        "required": true,
        "label": "State",
        "enabled": true,
        "fieldPath": "beneficialOwners.state",
        "section": "Beneficial Owners"
      },
      "corporationInfo.businessAddress": {
        "enabled": true,
        "placeholder": "Complete address",
        "section": "Corporation Information",
        "order": 12,
        "fieldName": "businessAddress",
        "fieldPath": "corporationInfo.businessAddress",
        "label": "Business Address",
        "helpText": "Your business address",
        "required": true
      },
      "beneficialOwners.firstName": {
        "placeholder": "John",
        "fieldName": "firstName",
        "required": true,
        "label": "First Name",
        "fieldPath": "beneficialOwners.firstName",
        "section": "Beneficial Owners",
        "enabled": true,
        "order": 1
      },
      "personalInfo.email": {
        "placeholder": "john@example.com",
        "enabled": true,
        "fieldName": "email",
        "label": "Email Address",
        "order": 3,
        "fieldPath": "personalInfo.email",
        "section": "Personal Information",
        "required": true
      },
      "corporationInfo.corporationType": {
        "fieldPath": "corporationInfo.corporationType",
        "label": "Corporation Type",
        "fieldName": "corporationType",
        "placeholder": "LLC, Corp, Partnership, etc.",
        "enabled": true,
        "order": 3,
        "section": "Corporation Information",
        "required": true
      },
      "corporationInfo.businessStartDate": {
        "fieldName": "businessStartDate",
        "section": "Corporation Information",
        "label": "Business Start Date",
        "enabled": true,
        "fieldPath": "corporationInfo.businessStartDate",
        "placeholder": "MM/DD/YYYY",
        "required": true,
        "order": 4
      },
      "corporationInfo.website": {
        "order": 6,
        "section": "Corporation Information",
        "linkEnabled": true,
        "fieldPath": "corporationInfo.website",
        "fieldName": "website",
        "placeholder": "https://example.com",
        "label": "Website",
        "required": true,
        "enabled": true
      },
      "bankDetails.bankName": {
        "fieldPath": "bankDetails.bankName",
        "fieldName": "bankName",
        "enabled": true,
        "required": true,
        "order": 1,
        "placeholder": "Bank of America",
        "section": "Bank Details",
        "label": "Bank Name"
      },
      "processingHistory.b2cPercentage": {
        "defaultValue": "50",
        "fieldName": "b2cPercentage",
        "placeholder": "50",
        "required": true,
        "label": "Business-to-Consumer Sales Percentage",
        "order": 15,
        "fieldPath": "processingHistory.b2cPercentage",
        "enabled": true,
        "helpText": "Percentage of sales to consumers (admin override)",
        "section": "Processing History"
      },
      "beneficialOwners.dateOfBirth": {
        "fieldPath": "beneficialOwners.dateOfBirth",
        "required": true,
        "placeholder": "MM/DD/YYYY",
        "enabled": true,
        "section": "Beneficial Owners",
        "label": "Date of Birth",
        "fieldName": "dateOfBirth",
        "order": 8
      },
      "processingHistory.retailPercentage": {
        "enabled": true,
        "fieldName": "retailPercentage",
        "fieldPath": "processingHistory.retailPercentage",
        "required": true,
        "defaultValue": "90",
        "placeholder": "90",
        "order": 12,
        "label": "Retail/Terminal Transaction Percentage",
        "helpText": "Percentage of transactions processed through physical terminals (shown only if offline cards accepted)",
        "section": "Processing History"
      },
      "corporationInfo.businessAddress.state": {
        "label": "State",
        "order": 15,
        "fieldPath": "corporationInfo.businessAddress.state",
        "enabled": true,
        "section": "Corporation Information",
        "placeholder": "CA",
        "required": true,
        "fieldName": "state"
      },
      "corporationInfo.customerServiceEmail": {
        "placeholder": "support@example.com",
        "section": "Corporation Information",
        "fieldName": "customerServiceEmail",
        "enabled": true,
        "fieldPath": "corporationInfo.customerServiceEmail",
        "order": 10,
        "label": "Customer Service Email",
        "linkEnabled": true,
        "required": true
      },
      "processingHistory.orderTurnaroundTime": {
        "defaultValue": "7",
        "enabled": true,
        "required": true,
        "fieldName": "orderTurnaroundTime",
        "order": 7,
        "label": "Average Order Turnaround (days)",
        "validation": {
          "min": 0
        },
        "placeholder": "7",
        "fieldPath": "processingHistory.orderTurnaroundTime",
        "helpText": "Typical number of days to fulfill or deliver an order.",
        "section": "Processing History"
      },
      "corporationInfo.businessAddress.zipCode": {
        "fieldName": "zipCode",
        "section": "Corporation Information",
        "enabled": true,
        "required": true,
        "fieldPath": "corporationInfo.businessAddress.zipCode",
        "placeholder": "12345",
        "label": "ZIP Code",
        "order": 16
      },
      "processingHistory.b2bPercentage": {
        "fieldPath": "processingHistory.b2bPercentage",
        "placeholder": "50",
        "label": "Business-to-Business Sales Percentage",
        "defaultValue": "50",
        "helpText": "Percentage of sales to other businesses (admin override)",
        "enabled": true,
        "order": 14,
        "fieldName": "b2bPercentage",
        "required": true,
        "section": "Processing History"
      },
      "beneficialOwners.title": {
        "section": "Beneficial Owners",
        "enabled": true,
        "fieldName": "title",
        "required": true,
        "fieldPath": "beneficialOwners.title",
        "order": 3,
        "label": "Title/Role",
        "helpText": "Role or title within the company (CEO, CFO, etc.)"
      },
      "processingHistory.hasBeenTerminated": {
        "placeholder": "Yes/No",
        "order": 16,
        "label": "Previously Terminated by a Processor?",
        "enabled": true,
        "fieldPath": "processingHistory.hasBeenTerminated",
        "fieldName": "hasBeenTerminated",
        "required": true,
        "section": "Processing History"
      },
      "beneficialOwners.email": {
        "required": true,
        "placeholder": "john@example.com",
        "order": 5,
        "fieldName": "email",
        "section": "Beneficial Owners",
        "enabled": true,
        "fieldPath": "beneficialOwners.email",
        "label": "Email Address"
      },
      "corporationInfo.sicCode": {
        "fieldPath": "corporationInfo.sicCode",
        "helpText": "Standard Industrial Classification or Merchant Category Code",
        "section": "Corporation Information",
        "order": 8,
        "required": true,
        "enabled": true,
        "fieldName": "sicCode",
        "placeholder": "7012",
        "label": "SIC/MCC Code"
      },
      "personalInfo.phone": {
        "fieldPath": "personalInfo.phone",
        "order": 4,
        "section": "Personal Information",
        "enabled": true,
        "label": "Phone Number",
        "fieldName": "phone",
        "placeholder": "(XXX) XXX-XXXX",
        "required": true
      }
    },
    "sectionConfigs": {
      "Beneficial Owners": {
        "title": "Beneficial Owners",
        "displayType": "page",
        "subtitle": "List all individuals who own 25% or more of the business.",
        "order": 4
      },
      "Personal Information": {
        "displayType": "page",
        "subtitle": "Let's start with some basic information about you. This will help us personalize your application experience.",
        "order": 1,
        "title": "Tell us about yourself"
      },
      "Bank Details": {
        "order": 5,
        "subtitle": "Provide your business banking information for fund deposits.",
        "displayType": "page",
        "title": "Bank Details"
      },
      "Corporation Information": {
        "title": "Business Information",
        "displayType": "page",
        "order": 2,
        "subtitle": "Tell us about your business and how it operates."
      },
      "Documentation": {
        "title": "Documentation",
        "order": 6,
        "subtitle": "Upload required documents to complete your application.",
        "displayType": "page"
      }
    }
  },
  "high-risk": {
    "sectionOrder": [
      "Personal Information",
      "Corporation Information",
      "Beneficial Owners",
      "Processing History",
      "Bank Details",
      "Documentation"
    ],
    "documentRequirements": [
      {
        "documentType": "drivers_license",
        "required": true,
        "maxFileSize": 5,
        "enabled": true,
        "label": "Driver's License",
        "description": "Valid government-issued driver's license",
        "acceptedFormats": ["jpg", "jpeg", "png", "pdf"]
      },
      {
        "description": "Voided check from your business bank account",
        "label": "Voided Check",
        "acceptedFormats": ["jpg", "jpeg", "png", "pdf"],
        "documentType": "voided_check",
        "enabled": true,
        "maxFileSize": 5,
        "required": true
      },
      {
        "label": "Bank Statements",
        "acceptedFormats": ["pdf"],
        "enabled": true,
        "description": "Last 3 months of business bank statements",
        "required": false,
        "documentType": "bank_statements",
        "processingVolumeThreshold": 50000,
        "maxFileSize": 10
      },
      {
        "label": "Business License",
        "description": "Business license or registration",
        "required": false,
        "documentType": "business_license",
        "maxFileSize": 5,
        "acceptedFormats": ["jpg", "jpeg", "png", "pdf"],
        "enabled": true
      },
      {
        "documentType": "tax_returns",
        "processingVolumeThreshold": 100000,
        "maxFileSize": 20,
        "acceptedFormats": ["pdf"],
        "enabled": false,
        "label": "Tax Returns",
        "description": "Last 2 years of business tax returns",
        "required": false
      },
      {
        "label": "Processing Statements",
        "documentType": "processing_statements",
        "enabled": true,
        "description": "Last 3 months of credit card processing statements (if currently processing)",
        "acceptedFormats": ["pdf"],
        "required": false,
        "maxFileSize": 10
      }
    ],
    "sectionVisibility": {
      "Personal Information": true,
      "Beneficial Owners": true,
      "Processing History": true,
      "Corporation Information": true,
      "Documentation": true,
      "Bank Details": true
    },
    "sectionConfigs": {
      "Bank Details": {
        "order": 5,
        "subtitle": "Provide your business banking information for fund deposits.",
        "displayType": "page",
        "title": "Bank Details"
      },
      "Beneficial Owners": {
        "order": 3,
        "title": "Beneficial Owners",
        "displayType": "page",
        "subtitle": "List all individuals who own 25% or more of the business."
      },
      "Corporation Information": {
        "subtitle": "Tell us about your business and how it operates.",
        "displayType": "page",
        "order": 2,
        "title": "Business Information"
      },
      "Personal Information": {
        "title": "Tell us about yourself",
        "order": 1,
        "displayType": "page",
        "subtitle": "Let's start with some basic information about you. This will help us personalize your application experience."
      },
      "Documentation": {
        "title": "Documentation",
        "order": 6,
        "displayType": "page",
        "subtitle": "Upload required documents to complete your application."
      },
      "Processing History": {
        "subtitle": "Help us understand your payment processing needs and history.",
        "title": "Processing History",
        "order": 4,
        "displayType": "page"
      }
    },
    "fieldSettings": {
      // High-risk has same field settings as low-risk (inherits the configuration)
      // Copying the same fieldSettings from low-risk
      ...(() => {
        // This will be populated with the same fields as low-risk
        // TypeScript will properly type this based on the low-risk config
        return {};
      })()
    }
  }
};

// Remove Processing History section assignment from low-risk fields BEFORE high-risk inherits
const processingHistoryFieldsToRemove = [
  'processingHistory.acceptOfflineCards',
  'processingHistory.advancedDeposits',
  'processingHistory.averageTicket',
  'processingHistory.b2bPercentage',
  'processingHistory.b2cPercentage',
  'processingHistory.bankruptcy',
  'processingHistory.currentProcessor',
  'processingHistory.depositPct',
  'processingHistory.hasBeenTerminated',
  'processingHistory.highTicket',
  'processingHistory.isCurrentlyProcessing',
  'processingHistory.monthlyVolume',
  'processingHistory.onlinePercentage',
  'processingHistory.orderTurnaroundTime',
  'processingHistory.retailPercentage',
  'processingHistory.salesDeliveryDays',
  'processingHistory.terminationExplanation',
  'processingHistory.whenIsCustomerCharged'
];

processingHistoryFieldsToRemove.forEach(fieldPath => {
  if (DEFAULT_BANKING_PARTNER_CONFIG['low-risk'].fieldSettings[fieldPath]) {
    // Set enabled to false AND remove section property
    DEFAULT_BANKING_PARTNER_CONFIG['low-risk'].fieldSettings[fieldPath].enabled = false;
    delete DEFAULT_BANKING_PARTNER_CONFIG['low-risk'].fieldSettings[fieldPath].section;
  }
});

// Helper function to ensure high-risk has the same field settings as low-risk
DEFAULT_BANKING_PARTNER_CONFIG['high-risk'].fieldSettings = {
  ...DEFAULT_BANKING_PARTNER_CONFIG['low-risk'].fieldSettings
};

// Add Processing History section assignment ONLY for high-risk fields
const processingHistoryFields = [
  'processingHistory.acceptOfflineCards',
  'processingHistory.advancedDeposits',
  'processingHistory.averageTicket',
  'processingHistory.b2bPercentage',
  'processingHistory.b2cPercentage',
  'processingHistory.bankruptcy',
  'processingHistory.currentProcessor',
  'processingHistory.depositPct',
  'processingHistory.hasBeenTerminated',
  'processingHistory.highTicket',
  'processingHistory.isCurrentlyProcessing',
  'processingHistory.monthlyVolume',
  'processingHistory.onlinePercentage',
  'processingHistory.orderTurnaroundTime',
  'processingHistory.retailPercentage',
  'processingHistory.salesDeliveryDays',
  'processingHistory.terminationExplanation',
  'processingHistory.whenIsCustomerCharged'
];

processingHistoryFields.forEach(fieldPath => {
  if (DEFAULT_BANKING_PARTNER_CONFIG['high-risk'].fieldSettings[fieldPath]) {
    DEFAULT_BANKING_PARTNER_CONFIG['high-risk'].fieldSettings[fieldPath].section = 'Processing History';
    DEFAULT_BANKING_PARTNER_CONFIG['high-risk'].fieldSettings[fieldPath].enabled = true;
  }
});
